import { Alert, AlertDescription, AlertIcon, Tab } from "@chakra-ui/react";
import { keepPreviousData } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import { CapitalEventStatsCard } from "features/capital-activity/event/CapitalEventStatsCard";
import { BorrowingBaseSummaryTableBody } from "features/credit-facility/borrowing-base/BorrowingBaseSummaryTableBody";
import {
  useCapitalCallBreakdownTableDataRetrieve,
  useCapitalCallUnfundedBreakdownTableDataRetrieve,
} from "gen/endpoints/capital-call/capital-call";
import { useFundFamilyEventCapitalCallReallocationBreakdownTableRetrieveSuspense } from "gen/endpoints/capital-call-reallocation-breakdown/capital-call-reallocation-breakdown";
import {
  useCreditFacilityListSuspense,
  useCreditFacilitySummaryRetrieve,
} from "gen/endpoints/credit-facility/credit-facility";
import { useFundFamilyEventRetrieveSuspense } from "gen/endpoints/fund-family-event/fund-family-event";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { noop } from "lodash-es";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { LoadingState } from "shared/components/empty/LoadingState";
import Form from "shared/components/form/Form";
import PageBody from "shared/components/layout/PageBody";
import { PageHeader } from "shared/components/layout/PageHeader";
import {
  PageWizardFormFooter,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTabs } from "shared/components/table/box/TableBoxTabs";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetColumnDefFn } from "shared/components/table/data/DataTableTypes";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { WarningIcon } from "shared/icons/WarningIcon";
import { formatMoneyFromDecimal } from "shared/utils/MoneyUtils";

export const CapitalCallBreakdownPage: React.FC = () => {
  const { fundFamilyId } = useRequiredStringParams();
  // we reuse the same page for the capital call breakdown and cap call reallocation breakdown and so we need to check if the reallocation id is present
  const { fundFamilyEventId: capitalCallId, reallocationFundFamilyEventId } =
    useParams();
  const [isEqualizedView, setIsEqualizedView] = useState(false);
  const fundFamilyEventId = reallocationFundFamilyEventId ?? capitalCallId!;

  const { data: eventData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const { data: creditFacilityList } =
    useCreditFacilityListSuspense(fundFamilyId);
  const creditFacilityId = creditFacilityList[0]?.id;
  const { data: creditFacilitySummaryData } = useCreditFacilitySummaryRetrieve(
    fundFamilyId,
    creditFacilityId,
    {
      fund_family_event_id: fundFamilyEventId,
    },
    {
      query: {
        enabled: !!creditFacilityId,
      },
    },
  );

  const { t } = useTranslation(["common", "capitalCall", "creditFacility"]);
  const { navigateNext } = usePageWizard();

  const form = useForm();
  const {
    outstanding_amount,
    borrowing_base_amount,
    has_borrowing_base_groups,
  } = creditFacilitySummaryData ?? {};
  const borrowingBaseAmount = new Decimal(borrowing_base_amount ?? "0");
  const outstandingAmount = new Decimal(outstanding_amount ?? "0");
  const workflowName = reallocationFundFamilyEventId
    ? t("capitalCall:reallocation.workflow")
    : t("capitalCall:capitalCall");
  return (
    <Form form={form} onSubmit={noop} onSuccess={navigateNext}>
      <PageHeader
        title={t("capitalCall:investorLevelBreakdown")}
        workflowName={workflowName}
        stats={
          <CapitalEventStatsCard
            capitalActivityData={eventData}
            isEqualizedView={isEqualizedView}
            setIsEqualized={setIsEqualizedView}
          />
        }
        badge={getCapitalActivityEventStateBadge(eventData.state, t, false)}
      />
      <PageBody>
        {has_borrowing_base_groups &&
          outstandingAmount.greaterThan(borrowingBaseAmount) && (
            <Alert status="warning" mb="16px">
              <AlertIcon>
                <WarningIcon />
              </AlertIcon>
              <AlertDescription>
                {t(
                  "creditFacility:borrowingBase.outstandingAmountGreaterThanBorrowingBase",
                  {
                    outstandingAmount:
                      formatMoneyFromDecimal(outstandingAmount),
                    borrowingBaseAmount:
                      formatMoneyFromDecimal(borrowingBaseAmount),
                  },
                )}
              </AlertDescription>
            </Alert>
          )}
        <CapitalCallBreakdownTable
          fundFamilyId={eventData.fund_family_id}
          fundFamilyEventId={eventData.id!}
          showBorrowingBaseTable={
            borrowingBaseAmount.greaterThan(0) && !!has_borrowing_base_groups
          }
          creditFacilityId={creditFacilityId}
          isEqualizedView={isEqualizedView}
        />
      </PageBody>
      <PageWizardFormFooter
        submitPermission={
          reallocationFundFamilyEventId
            ? PermissionTypeEnum.approve
            : PermissionTypeEnum.read
        }
      />
    </Form>
  );
};

interface CapitalCallBreakdownTableProps {
  tabColorScheme?: string;
  fundFamilyId: string;
  fundFamilyEventId: string;
  showBorrowingBaseTable: boolean;
  creditFacilityId?: string;
  isEqualizedView: boolean;
}

export const CapitalCallBreakdownTable: React.FC<
  CapitalCallBreakdownTableProps
> = ({
  tabColorScheme,
  fundFamilyId,
  fundFamilyEventId,
  showBorrowingBaseTable,
  creditFacilityId,
  isEqualizedView,
}) => {
  const { data: breakdownTableData, isFetching: isLoadingBreakdown } =
    useCapitalCallBreakdownTableDataRetrieve(
      fundFamilyId,
      fundFamilyEventId,
      {
        is_equalized_view: isEqualizedView || undefined,
      },
      {
        query: {
          placeholderData: keepPreviousData,
        },
      },
    );
  const {
    data: unfundedBreakdownTableData,
    isFetching: isLoadingUnfundedBreakdown,
  } = useCapitalCallUnfundedBreakdownTableDataRetrieve(
    fundFamilyId,
    fundFamilyEventId,
    {
      is_equalized_view: isEqualizedView || undefined,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const { data: reallocationBreakdownTableData } =
    useFundFamilyEventCapitalCallReallocationBreakdownTableRetrieveSuspense(
      fundFamilyId,
      fundFamilyEventId,
    );

  const { t } = useTranslation(["capitalCall", "common"]);
  const [selectedTab, setSelectedTab] = useState(0);

  const tableData = [breakdownTableData, unfundedBreakdownTableData];
  const loadingData = [isLoadingBreakdown, isLoadingUnfundedBreakdown];
  const currentIsLoading = loadingData[selectedTab];
  const currentTableData = tableData[selectedTab];
  const showReallocationTab =
    isEqualizedView && reallocationBreakdownTableData.rows.length > 0;

  return (
    <TableBox isFullWidth={true}>
      <TableBoxHeader>
        <TableBoxTabs
          index={selectedTab}
          onChange={setSelectedTab}
          colorScheme={tabColorScheme}
        >
          <Tab>
            {t("capitalCall:breakdownViewHeaders.capitalCallsBreakdownView")}
          </Tab>
          <Tab>
            {t(
              "capitalCall:breakdownViewHeaders.unfundedCommitmentBreakdownView",
            )}
          </Tab>
          {showBorrowingBaseTable && (
            <Tab>{t("capitalCall:breakdownViewHeaders.borrowingBase")}</Tab>
          )}
          {showReallocationTab && (
            <Tab>{t("capitalCall:breakdownViewHeaders.reallocationView")}</Tab>
          )}
        </TableBoxTabs>
        <DataTableBoxActions />
      </TableBoxHeader>
      {showBorrowingBaseTable && selectedTab === 2 ? (
        <QueryBoundary>
          <BorrowingBaseSummaryTableBody
            creditFacilityId={creditFacilityId!}
            fundFamilyEventId={fundFamilyEventId}
          />
        </QueryBoundary>
      ) : showReallocationTab && selectedTab === 3 ? (
        <TableBoxBody>
          <DataTable data={reallocationBreakdownTableData} />
        </TableBoxBody>
      ) : (
        <TableBoxBody>
          {currentTableData ? (
            <DataTable
              getColumnDef={getColumnDef}
              data={currentTableData}
              isLoading={currentIsLoading}
            />
          ) : (
            <LoadingState />
          )}
        </TableBoxBody>
      )}
    </TableBox>
  );
};

const getColumnDef: GetColumnDefFn = ({ column, rootColumn }) => {
  if (
    rootColumn?.id === "fees" &&
    column.id !== "fees" &&
    (column.title?.length ?? 0) > 10
  ) {
    return {
      width: 220,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    };
  }
};
